import { request } from '@mairistem/tools';
import type {} from '@redux-devtools/extension'; // required for devtools typing
import _ from 'lodash';
import { create } from 'zustand';
import { createJSONStorage, devtools, persist } from 'zustand/middleware';
import { getRegie, getRegies } from '@/routes';
import type { ApiResponseType, RegieType } from '@/types';

export type RegieInStoreType = Pick<
  RegieType,
  | 'identifiant'
  | 'titre'
  | 'codeInterne'
  | 'regisseurConnexion'
  | 'ecole'
  | 'autonome'
  | 'dateArrete'
  | 'dateFinDernierArrete'
  | 'typeDernierArrete'
  | 'dateProduction'
  | 'nombreReglementsAVerser'
  | 'periodiciteArrete'
  | 'periodiciteVersementEncaisseGlobale'
  | 'regieProlongee'
  | 'joursEncaissementSupplementaires'
  | 'encaissementPossible'
  | 'annulationPossible'
  | 'arreteAnnuelRequis'
  | 'arreteAnnuelCas'
  | 'arreteAnnuelAnnee'
  | 'arreteConfig'
>;

type RegieStoreType = {
  disabledMenus: Record<string, boolean>;
  updateDisabledMenus: (regie: RegieInStoreType) => void;
  regie: null | RegieInStoreType;
  setRegie: (regie: RegieInStoreType) => void;
  clearRegie: () => void;
  updateRegie: (regie: Partial<RegieInStoreType>) => void;
  /** Re-fetch la régie courante (GET /regies/{id}) et met à jour les champs volatils du store. */
  refreshRegie: () => Promise<void>;
  hasRegies: null | boolean;
  setHasRegies: (hasRegies?: boolean) => void;
};

const useRegieStore = create<RegieStoreType>()(
  devtools(
    persist(
      (set, get) => ({
        hasRegies: null,
        setHasRegies: (hasRegies?: boolean) => {
          if (_.isNil(hasRegies)) {
            request.get(getRegies()).then(({ data }: ApiResponseType<RegieType>) => {
              set({ hasRegies: !_.isEmpty(data) });
            });
          } else {
            set({ hasRegies });
          }
        },
        regie: null,
        setRegie: async (regie) => {
          set({ regie });

          get()?.updateDisabledMenus(regie);
        },
        clearRegie: () => {
          set({ regie: null });
        },
        disabledMenus: {},
        updateRegie: (partialRegie) => {
          const currentRegie = get().regie;
          if (currentRegie) {
            set({ regie: { ...currentRegie, ...partialRegie } });
          }
        },
        refreshRegie: async () => {
          const currentRegie = get().regie;
          if (!currentRegie?.identifiant) return;

          const response = (await request.get(getRegie(currentRegie.identifiant))) as {
            data: RegieType;
          };
          const data = response?.data;
          if (!data) return;

          // Mise à jour des champs volatils impactés par la procédure d'arrêté annuel.
          get().updateRegie({
            dateArrete: data.dateArrete,
            dateFinDernierArrete: data.dateFinDernierArrete,
            typeDernierArrete: data.typeDernierArrete,
            nombreReglementsAVerser: data.nombreReglementsAVerser,
            encaissementPossible: data.encaissementPossible,
            annulationPossible: data.annulationPossible,
            arreteAnnuelRequis: data.arreteAnnuelRequis,
            arreteAnnuelCas: data.arreteAnnuelCas,
            arreteAnnuelAnnee: data.arreteAnnuelAnnee,
            arreteConfig: data.arreteConfig,
          });
        },
        updateDisabledMenus: async (/* regie */) => {
          // const { data: modesReglement }: ApiResponseType<RegieType> = await request.get(
          //   getRegieModesReglement({ identifiantRegie: regie.identifiant }),
          // );
          // set({
          //   disabledMenus: {
          //     prelevement: !_.some(modesReglement, { code: PRELEVEMENT }),
          //   },
          // });
        },
      }),
      {
        name: 'regie',
        storage: createJSONStorage(() => sessionStorage),
      },
    ),
  ),
);

export default useRegieStore;
